import React, { useRef, type FocusEvent, useEffect, memo, type MouseEvent } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration';
import { ff } from '@atlassian/jira-feature-flagging';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler';
import { useEvent } from '@atlassian/jira-software-react-use-event';
import traceUFOInteraction from '@atlassian/ufo-trace-interaction';
import type { CardId } from '../../../model/types';
import Card from '../card';
import InlineCardCreate from '../inline-card-create';
import { getActiveElement } from './util';

export type Props = {
	setRichActiveCard: (cardId: number | null) => void;
	isActiveSprint: boolean;
	isIccEnabled: boolean;
	draggableIndex: number;
	canShowStatusLozengeOnCards: boolean;
	cardListId: string;
	cardId: CardId;
	shouldRenderRichCard: boolean;
	isVisible: boolean;
	hasIccOpen: boolean;
	isAttachedToParent?: boolean;
	subtaskIds: CardId[] | undefined;
};

const CardWithIccWrapperInner = ({
	cardId,
	cardListId,
	isVisible,
	shouldRenderRichCard,
	hasIccOpen,
	setRichActiveCard,
	isIccEnabled,
	draggableIndex,
	canShowStatusLozengeOnCards,
	isAttachedToParent,
	subtaskIds,
}: Props) => {
	const containerRef = useRef<HTMLElement | null>(null);
	const hasRenderedRichElements = useRef(false);
	const pendingHoverAction = useRef<ReturnType<typeof setTimeout> | undefined>();

	useEffect(() => () => clearTimeout(pendingHoverAction.current), [pendingHoverAction]);

	const autobatch = useAutobatch();
	const onMouseEnter = useEvent((e: MouseEvent) => {
		autobatch(() => {
			traceUFOInteraction('backlog.card-hover', e);
			setRichActiveCard(cardId);
		});
	});

	const onFocus = useEvent(() => {
		setRichActiveCard(cardId);
	});

	const onMouseLeave = useEvent(() => {
		const containerElement = containerRef.current;
		if (!containerElement) {
			return;
		}

		// Remove pending setRichActiveCard if it hasn't already fired
		if (pendingHoverAction) {
			clearTimeout(pendingHoverAction.current);
			pendingHoverAction.current = undefined;
		}

		const activeElement = getActiveElement();
		// Ignore event if focused on a child
		if (activeElement instanceof Node && containerElement.contains(activeElement)) {
			return;
		}

		setRichActiveCard(null);
	});

	const onBlur = useEvent((e: FocusEvent<HTMLElement>) => {
		const { relatedTarget } = e;
		if (
			containerRef.current &&
			relatedTarget instanceof Node &&
			!containerRef.current.contains(relatedTarget)
		) {
			setRichActiveCard(null);
		}
	});

	if (shouldRenderRichCard && !hasRenderedRichElements.current) {
		hasRenderedRichElements.current = true;
	}

	// First card on the list always renders ICC above itself so tab works from the list header
	const canUseICC = isIccEnabled && cardId > 0;
	const shouldRenderICCAbove =
		canUseICC &&
		(draggableIndex === 0 || hasIccOpen || shouldRenderRichCard || hasRenderedRichElements.current);

	return (
		<CardWithICC
			isVisible={isVisible}
			innerRef={containerRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			{shouldRenderICCAbove && (
				<InlineCardCreate listId={cardListId} insertBefore={cardId} isBetweenCards />
			)}
			<Card
				id={cardId}
				cardListId={cardListId}
				canShowStatus={canShowStatusLozengeOnCards}
				draggableIndex={draggableIndex}
				shouldRenderRichCard={shouldRenderRichCard || hasRenderedRichElements.current}
				isAttachedToParent={isAttachedToParent}
				subtaskIds={subtaskIds}
			/>
		</CardWithICC>
	);
};

export const CardWithIccWrapper = memo(CardWithIccWrapperInner);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const CardWithICCControl = styled.div<{ isVisible?: boolean }>((props) => ({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	display: props.isVisible ? 'block' : 'none',
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardWithICCExperiment = styled2.div<{ isVisible?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.isVisible ? 'block' : 'none'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CardWithICC = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	CardWithICCExperiment,
	CardWithICCControl,
);
